import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Styles.css"; 
import { auth } from "../../firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";

const API_URL = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || 'https://ceindiazone2.in');

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [userInput, setUserInput] = useState("");
    const [message, setMessage] = useState("");

    // UI State for OTP and Password View
    const [isOtpSent, setIsOtpSent] = useState(false);
    const [isOtpVerified, setIsOtpVerified] = useState(false);
    const [otp, setOtp] = useState("");
    const [timer, setTimer] = useState(0);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    // Timer Effect
    useEffect(() => {
        let interval;
        if (isOtpSent && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else if (timer === 0) {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [isOtpSent, timer]);

    const setupRecaptcha = () => {
        if (!window.recaptchaVerifier) {
            window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
                'size': 'invisible',
                'callback': (response) => {
                    // reCAPTCHA solved
                }
            });
        }
    };

    const handleReset = async (e) => {
        e.preventDefault();
        setMessage("");
        setIsLoading(true);

        const isPhone = !userInput.includes("@") && /\d/.test(userInput);
        
        if (isPhone) {
            try {
                setupRecaptcha();
                let formattedPhone = userInput.trim();
                if (!formattedPhone.startsWith("+")) {
                    formattedPhone = "+91" + formattedPhone;
                }
                
                const appVerifier = window.recaptchaVerifier;
                const result = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
                setConfirmationResult(result);
                setIsOtpSent(true);
                setIsOtpVerified(false);
                setTimer(60);
                setMessage("OTP sent via SMS. Please check your messages.");
            } catch (err) {
                console.error("Phone OTP send failed", err);
                setMessage("Failed to send OTP. Please ensure your number is correct or try again later.");
                if (window.recaptchaVerifier) {
                    window.recaptchaVerifier.render().then(function(widgetId) {
                        window.grecaptcha.reset(widgetId);
                    });
                }
            } finally {
                setIsLoading(false);
            }
        } else {
            const payload = {
                email: userInput,
                phone: null
            };

            try {
                const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });
                
                const data = await res.text();
                if (!res.ok) throw new Error(data || "Backend failed");

                setMessage("Password reset link sent to your email successfully.");
            } catch (err) {
                console.error("Reset failed", err);
                setMessage(err.message.length < 100 ? `Failed to send reset link: ${err.message}` : "Failed to send reset link.");
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setMessage("");
        setIsLoading(true);
        try {
            await confirmationResult.confirm(otp);
            setTimer(0);
            setIsOtpVerified(true);
            setMessage("Phone verified successfully.");
        } catch (err) {
            console.error("OTP verification failed", err);
            setMessage("Invalid OTP. Please try again.");
            setIsOtpVerified(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setMessage("Passwords do not match.");
            return;
        }

        setIsLoading(true);
        try {
            let formattedPhone = userInput.trim();
            if (!formattedPhone.startsWith("+")) {
                formattedPhone = "+91" + formattedPhone;
            }
            
            const payload = {
                phone: formattedPhone,
                newPassword: newPassword
            };
            
            const res = await fetch(`${API_URL}/api/auth/reset-password-firebase-phone`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });
            
            const data = await res.text();
            if (!res.ok) {
                throw new Error(data || "Password reset failed");
            }
            
            setMessage("Password reset successfully! Please check your email for confirmation.");
            setTimeout(() => {
                navigate("/signIn");
            }, 3000);
        } catch (err) {
            console.error("Update password failed:", err);
            setMessage(err.message.length < 100 ? `Failed to update password: ${err.message}` : "Failed to update password.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="signin-container">
            {/* Simplify for Forgot Password - reuse container classes for layout consistency */}
            <div className="signin-left">
                <div className="signin-left-content">
                    <h1>Reset Password</h1>
                    <p>{!isOtpSent
                        ? "Don't worry, it happens! Enter your email or phone to reset."
                        : !isOtpVerified
                            ? "We've sent an OTP to verify your identity."
                            : "Create a new strong password for your account."}</p>
                </div>
            </div>

            <div className="signin-right">
                <h2>{!isOtpSent ? "Forgot Password" : !isOtpVerified ? "Verify OTP" : "Create New Password"}</h2>

                {!isOtpSent ? (
                    <form onSubmit={handleReset}>
                        <div className="form-group">
                            <input
                                type="text"
                                placeholder="Enter Email or Phone Number"
                                value={userInput}
                                onChange={(e) => setUserInput(e.target.value)}
                                required
                            />
                        </div>
                        <div id="recaptcha-container"></div>
                        <button type="submit" className="login-btn" disabled={isLoading}>
                            {isLoading ? "Processing..." : "Send Reset Link / OTP"}
                        </button>
                    </form>
                ) : !isOtpVerified ? (
                    <form onSubmit={handleVerifyOtp}>
                        <div className="form-group" style={{ textAlign: "center" }}>
                            <input
                                type="text"
                                placeholder="Enter 6-digit OTP"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                maxLength={6}
                                required
                                style={{ textAlign: "center", letterSpacing: "5px", fontSize: "1.2rem" }}
                            />
                        </div>
                        <button type="submit" className="login-btn" disabled={isLoading}>
                            {isLoading ? "Verifying..." : "Verify OTP"}
                        </button>
                        <div style={{ textAlign: "center", marginTop: "15px", fontSize: "0.9rem", color: "#aaa" }}>
                            {timer > 0 ? (
                                <p>Resend OTP in <span style={{ color: "#FED557", fontWeight: "bold" }}>{timer}s</span></p>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleReset}
                                    style={{ background: "none", border: "none", color: "#FED557", cursor: "pointer", textDecoration: "underline" }}
                                    disabled={isLoading}
                                >
                                    Resend OTP now
                                </button>
                            )}
                        </div>
                    </form>
                ) : (
                    <form onSubmit={handleUpdatePassword}>
                        <div className="form-group">
                            <input
                                type={showNewPassword ? "text" : "password"}
                                placeholder="New Password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="has-eye-icon"
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                            >
                                {showNewPassword ? "🙈" : "👁️"}
                            </button>
                        </div>
                        <div className="form-group">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Confirm New Password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="has-eye-icon"
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? "🙈" : "👁️"}
                            </button>
                        </div>
                        <button type="submit" className="login-btn" disabled={isLoading}>
                            {isLoading ? "Updating..." : "Update Password"}
                        </button>
                    </form>
                )}

                {message && <p style={{ marginTop: "1rem", textAlign: "center", color: message.toLowerCase().includes("fail") || message.toLowerCase().includes("please") ? "red" : "#28a745" }}>{message}</p>}

                <p className="or-text">Back to <span> <Link to="/signin" className="toggle-btn">
                    Sign In
                </Link></span></p>
            </div>
        </div>
    );
};

export default ForgotPassword;
